import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { apiClient } from '../../lib/api'
import { useAuthStore } from '../auth/auth.store'
import { CART_ENDPOINTS } from './cart.endpoints'
import type { AddToCartPayload, ApiCartItem, CartActionResponse, CartResponse } from './cart.types'
import { useCartStore } from './useCartStore'

// Helper to resolve cart_id: ONLY pass cart_id for unauthenticated guests.
// Authenticated user carts are tracked automatically by the server using their JWT session.
const getEffectiveCartId = (cartId?: string | null): string | undefined => {
  const customer = useAuthStore.getState().customer
  if (customer) {
    return undefined
  }
  return cartId || useCartStore.getState().cartId || undefined
}

// --- Service Functions ---

export const getCartService = async (cartId?: string | null): Promise<CartResponse> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.get<CartResponse>(CART_ENDPOINTS.GET_CART, { params })
  return data
}

export const addToCartService = async (payload: AddToCartPayload): Promise<CartResponse> => {
  const effectiveId = getEffectiveCartId(payload.cart_id)
  const { data } = await apiClient.post<CartResponse>(CART_ENDPOINTS.ADD_ITEM, {
    product_slug: payload.product_slug,
    ...(effectiveId ? { cart_id: effectiveId } : {}),
  })
  return data
}

export const clearCartService = async (cartId?: string | null): Promise<{ message: string }> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.delete<{ message: string }>(CART_ENDPOINTS.CLEAR_CART, { params })
  return data
}

export const getCartItemService = async (itemId: string | number, cartId?: string | null): Promise<ApiCartItem> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.get<ApiCartItem>(CART_ENDPOINTS.ITEM_DETAILS(itemId), { params })
  return data
}

export const removeCartItemService = async (
  itemId: string | number,
  cartId?: string | null,
): Promise<{ message: string }> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.delete<{ message: string }>(CART_ENDPOINTS.REMOVE_ITEM(itemId), { params })
  return data
}

export const incrementCartItemService = async (
  itemId: string | number,
  quantity = 1,
  cartId?: string | null,
): Promise<CartActionResponse> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.post<CartActionResponse>(
    CART_ENDPOINTS.INCREMENT_ITEM(itemId),
    { quantity },
    { params },
  )
  return data
}

export const decrementCartItemService = async (
  itemId: string | number,
  quantity = 1,
  cartId?: string | null,
): Promise<CartActionResponse> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.post<CartActionResponse>(
    CART_ENDPOINTS.DECREMENT_ITEM(itemId),
    { quantity },
    { params },
  )
  return data
}

export const checkAllCartItemsService = async (cartId?: string | null): Promise<CartResponse> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.post<CartResponse>(CART_ENDPOINTS.CHECK_ALL, {}, { params })
  return data
}

export const uncheckAllCartItemsService = async (cartId?: string | null): Promise<CartResponse> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.post<CartResponse>(CART_ENDPOINTS.UNCHECK_ALL, {}, { params })
  return data
}

export const checkCartItemService = async (itemId: string | number, cartId?: string | null): Promise<CartResponse> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.post<CartResponse>(CART_ENDPOINTS.CHECK_ITEM(itemId), {}, { params })
  return data
}

export const uncheckCartItemService = async (
  itemId: string | number,
  cartId?: string | null,
): Promise<CartResponse> => {
  const effectiveId = getEffectiveCartId(cartId)
  const params = effectiveId ? { cart_id: effectiveId } : undefined
  const { data } = await apiClient.post<CartResponse>(CART_ENDPOINTS.UNCHECK_ITEM(itemId), {}, { params })
  return data
}

export const CART_QUERY_KEY = ['cart'] as const

// --- Query Hooks ---

export const useCartQuery = () => {
  const cartId = useCartStore((state) => state.cartId)
  const setCartId = useCartStore((state) => state.setCartId)
  const setApiCart = useCartStore((state) => state.setApiCart)
  const customer = useAuthStore((state) => state.customer)
  const isAuthenticated = !!customer

  const effectiveCartId = isAuthenticated ? undefined : cartId || undefined

  const query = useQuery({
    queryKey: CART_QUERY_KEY,
    queryFn: () => getCartService(effectiveCartId),
    retry: 1, // Don't retry endlessly if the cart is invalid for some reason
  })

  // Sync the cart ID to local storage when an anonymous cart is created
  useEffect(() => {
    if (query.data) {
      // If backend returns a cart_id (guest user), persist it
      if (!isAuthenticated && query.data.cart_id && query.data.cart_id !== cartId) {
        setCartId(query.data.cart_id)
      } else if (isAuthenticated && cartId) {
        setCartId(null)
      }

      // Update the local API cart state for UI totals
      setApiCart(query.data)
    }
  }, [query.data, setCartId, setApiCart, cartId, isAuthenticated])

  return query
}

// --- Mutation Hooks (Optimistic Updates) ---

export const useAddToCart = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setCartId = useCartStore((state) => state.setCartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation({
    mutationFn: (productSlug: string) => addToCartService({ product_slug: productSlug, cart_id: cartId }),
    onMutate: async (productSlug: string) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      if (previous) {
        const existingItem = previous.items.find((i) => i.product.product_slug === productSlug)
        if (existingItem) {
          const updated: CartResponse = {
            ...previous,
            items: previous.items.map((i) =>
              i.product.product_slug === productSlug
                ? { ...i, quantity: Math.min(i.quantity + 1, i.product.stockleft) }
                : i,
            ),
          }
          queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, updated)
          setApiCart(updated)
        }
      }

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
    },
    onSuccess: (data) => {
      // Persist guest cart ID if newly created
      if (data.cart_id && data.cart_id !== cartId) {
        setCartId(data.cart_id)
      }
      setApiCart(data)
      // Replace optimistic data with real server response
      queryClient.setQueryData(CART_QUERY_KEY, data)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
    },
  })
}

export const useClearCart = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation({
    mutationFn: () => clearCartService(cartId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const updated: CartResponse = { ...old, items: [], cart_subtotal: 0, total_vat: 0, grand_total: 0 }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSuccess: () => {
      setApiCart(null)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
    },
  })
}

export const useItemDetails = (itemId: string | number, enabled = true) => {
  const cartId = useCartStore((state) => state.cartId)
  return useQuery({
    queryKey: ['cartItem', itemId],
    queryFn: () => getCartItemService(itemId, cartId),
    enabled: !!itemId && enabled,
  })
}

export const useRemoveCartItem = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation<{ message: string }, Error, string | number, { previous: CartResponse | undefined }>({
    mutationFn: (itemId) => removeCartItemService(itemId, cartId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const filtered = old.items.filter((i) => i.id !== itemId)
        const updated: CartResponse = { ...old, items: filtered }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _itemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSettled: () => {
      // Always sync after remove — the item is gone regardless
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
    },
  })
}

export const useIncrementCartItem = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation<CartActionResponse, Error, string | number, { previous: CartResponse | undefined }>({
    mutationFn: (itemId) => incrementCartItemService(itemId, 1, cartId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const updated: CartResponse = {
          ...old,
          items: old.items.map((i) => {
            if (i.id !== itemId) return i
            const ceiling = i.product.stockleft
            const newQty = Math.min(i.quantity + 1, ceiling)
            return { ...i, quantity: newQty }
          }),
        }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _itemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSettled: () => {
      if (queryClient.isMutating() <= 1) {
        queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
      }
    },
  })
}

export const useDecrementCartItem = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation<CartActionResponse, Error, string | number, { previous: CartResponse | undefined }>({
    mutationFn: (itemId) => decrementCartItemService(itemId, 1, cartId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const updated: CartResponse = {
          ...old,
          items: old.items.map((i) => (i.id === itemId ? { ...i, quantity: Math.max(1, i.quantity - 1) } : i)),
        }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _itemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSettled: () => {
      if (queryClient.isMutating() <= 1) {
        queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
      }
    },
  })
}

export const useCheckCartItem = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation<CartResponse, Error, string | number, { previous: CartResponse | undefined }>({
    mutationFn: (itemId) => checkCartItemService(itemId, cartId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const updated: CartResponse = {
          ...old,
          items: old.items.map((i) => (i.id === itemId ? { ...i, is_checked: true } : i)),
        }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _itemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSettled: () => {
      if (queryClient.isMutating() <= 1) {
        queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
      }
    },
  })
}

export const useUncheckCartItem = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation<CartResponse, Error, string | number, { previous: CartResponse | undefined }>({
    mutationFn: (itemId) => uncheckCartItemService(itemId, cartId),
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const updated: CartResponse = {
          ...old,
          items: old.items.map((i) => (i.id === itemId ? { ...i, is_checked: false } : i)),
        }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _itemId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSettled: () => {
      if (queryClient.isMutating() <= 1) {
        queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
        queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
      }
    },
  })
}

export const useCheckAllCartItems = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation<CartResponse, Error, void, { previous: CartResponse | undefined }>({
    mutationFn: () => checkAllCartItemsService(cartId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const updated: CartResponse = {
          ...old,
          items: old.items.map((i) => ({ ...i, is_checked: true })),
        }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
    },
  })
}

export const useUncheckAllCartItems = () => {
  const queryClient = useQueryClient()
  const cartId = useCartStore((state) => state.cartId)
  const setApiCart = useCartStore((state) => state.setApiCart)

  return useMutation<CartResponse, Error, void, { previous: CartResponse | undefined }>({
    mutationFn: () => uncheckAllCartItemsService(cartId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: CART_QUERY_KEY })
      const previous = queryClient.getQueryData<CartResponse>(CART_QUERY_KEY)

      queryClient.setQueryData<CartResponse>(CART_QUERY_KEY, (old) => {
        if (!old) return old
        const updated: CartResponse = {
          ...old,
          items: old.items.map((i) => ({ ...i, is_checked: false })),
        }
        setApiCart(updated)
        return updated
      })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(CART_QUERY_KEY, context.previous)
        setApiCart(context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: CART_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['orderPreview'] })
    },
  })
}
